import { Button, Card, InlineStack, Layout, Page, Text, TextField, BlockStack } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import ActivityLog from '#client/components/ActivityLog';
import { inventoryTransferService } from '#client/services/inventoryTransferServices';
import { useToast } from '#client/components/common/Toast/ToastContext';
import StoreLocation from '#client/components/StoreLocation';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import TRANSFER_STRINGS from '#client/pages/Transfers/constants/transfers.en.json';
import { useTransferStore } from '#client/stores/transfer';
import { hasPermission } from '#client/utils/helpers';

import StoreAddress from '#client/components/StoreAddress';

import TransferIndexTable from '#client/pages/Transfers/TransferDashboardTable';

// Column configuration
const GLOBAL_COLUMN_CONFIG = [
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TRANSFER_NUMBER, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TRANSFER_NUMBER, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.STATUS, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.STATUS, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TYPE, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TYPE, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.FROM_LOCATION, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.FROM_LOCATION, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TO_LOCATION, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TO_LOCATION, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_SHIPPED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_SHIPPED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_RECEIVED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_RECEIVED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_CREATED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_CREATED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.LAST_UPDATED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.LAST_UPDATED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_SHIPPED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_SHIPPED, sortable: true },
    { key: TRANSFER_STRINGS.ACTIONS, title: TRANSFER_STRINGS.ACTIONS, sortable: false },
];

const INCOMING_COLUMN_CONFIG = [
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TRANSFER_NUMBER, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TRANSFER_NUMBER, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.STATUS, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.STATUS, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TYPE, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TYPE, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.FROM_LOCATION, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.FROM_LOCATION, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_SHIPPED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_SHIPPED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_RECEIVED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_RECEIVED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_CREATED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_CREATED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.LAST_UPDATED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.LAST_UPDATED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_SHIPPED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_SHIPPED, sortable: true },
    { key: TRANSFER_STRINGS.ACTIONS, title: TRANSFER_STRINGS.ACTIONS, sortable: false },
];

const OUTGOING_COLUMN_CONFIG = [
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TRANSFER_NUMBER, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TRANSFER_NUMBER, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.STATUS, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.STATUS, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TYPE, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TYPE, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TO_LOCATION, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.TO_LOCATION, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_SHIPPED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_SHIPPED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_RECEIVED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.QTY_RECEIVED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_CREATED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_CREATED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.LAST_UPDATED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.LAST_UPDATED, sortable: true },
    { key: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_SHIPPED, title: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.COLUMN_CONFIG.DATE_SHIPPED, sortable: true },
    { key: TRANSFER_STRINGS.ACTIONS, title: TRANSFER_STRINGS.ACTIONS, sortable: false },
];

export default function TransfersDashboard() {
    const navigate = useNavigate();
    const { currentUser } = useContext(SessionContext);
    const resetForm = useTransferStore.use.resetForm();
    const [transferName, setTransferName] = useState('');
    let scannedValue = useRef();
    const { addToast } = useToast();

    let timerRef = { current: 0 };
    let scannedRef = { current: '' };

    // Handle transfer creation
    const handleCreateTransfer = useCallback(() => {
        resetForm('create'); // Reset form state for new transfer
        navigate('/transfers/create');
    }, []);

    useEffect(() => {
        const handleKeyPress = async (event) => {
            // Collect characters from barcode scanner

            if (typeof event.key === 'string' && event.key.length === 1) {
                scannedRef.current += event.key;

                // Reset buffer timer on each keypress
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    scannedRef.current = '';
                }, 300);
            }

            // Process complete barcode when Enter is pressed
            if (event.key === 'Enter' && scannedRef.current) {
                scannedValue = scannedRef.current.trim();
                scannedRef.current = '';

                if (!scannedValue.startsWith('TR')) {
                    return;
                }

                try {
                    await handleTransferSearch(scannedValue);
                } catch (error) {
                    // Set appropriate error message based on mode
                    const errorMessage = TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.MISSING_SKU_OR_BARCODE_ERROR;

                    addToast({
                        type: 'error',
                        message: errorMessage,
                    });
                } finally {
                    //setLoading(false);
                }
            }
        };

        // Add event listener for barcode scanner
        window.addEventListener('keypress', handleKeyPress);

        // Cleanup function
        return () => {
            window.removeEventListener('keypress', handleKeyPress);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    });

    const activeStore = localStorage.getItem('activeStore') != 'ALL' ? localStorage.getItem('activeStore') : currentUser.locations.length == 1 ? currentUser.locations[0].locationId : null;

    const handleTransferSearch = async (scannedValue) => {
        if (transferName != '' || scannedValue) {
            var name = transferName != '' ? transferName : scannedValue;

            const response = await inventoryTransferService.fetchTransferByName(name.trim());
            if (response.data.transfer) {
                return navigate(`/transfers/view/${response.data.transfer.id}`);
            } else {
                addToast({
                    type: 'error',
                    message: `The requested transfer '${name.trim()}' could not be located`,
                });
            }
        }
    };

    // Generate table headings from column config
    const TABLE_HEADINGS = GLOBAL_COLUMN_CONFIG.map((column) => ({
        title: column.title,
    }));
    const INCOMING_TABLE_HEADINGS = INCOMING_COLUMN_CONFIG.map((column) => ({
        title: column.title,
    }));
    const OUTGOING_TABLE_HEADINGS = OUTGOING_COLUMN_CONFIG.map((column) => ({
        title: column.title,
    }));

    let createTransferButton;
    if (hasPermission(currentUser, 'transferCreate')) {
        createTransferButton = (
            <Button variant="primary" onClick={handleCreateTransfer}>
                Create New Transfer
            </Button>
        );
    }

    return (
        <div className="purchase-order-dashboard">
            <Page fullWidth primaryAction={createTransferButton}>
                <Layout>
                    {activeStore && (
                        <Layout.Section>
                            <Card>
                                <InlineStack gap="200">
                                    <Text variant="headingLg" as="h2">
                                        Transfers at Store: <StoreLocation locationId={activeStore} />
                                    </Text>
                                </InlineStack>
                                <InlineStack gap="200">
                                    <Text variant="headingSm" as="">
                                        <StoreAddress locationId={activeStore} />
                                    </Text>
                                </InlineStack>
                            </Card>
                        </Layout.Section>
                    )}
                    <Layout.Section>
                        <Card>
                            <BlockStack align="space-between">
                                <Text variant="headingLg" as="h2">
                                    Store to Store Transfer Lookup
                                </Text>
                            </BlockStack>
                            <BlockStack align="space-between">
                                Enter Transfer ID / Scan Packing Slip Barcode
                                <div
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleTransferSearch();
                                        }
                                    }}
                                >
                                    <TextField
                                        value={transferName}
                                        onChange={setTransferName}
                                        autoComplete="off"
                                        suffix={<Button icon={SearchIcon} disabled={transferName == ''} onClick={() => handleTransferSearch()} />}
                                    />
                                </div>
                            </BlockStack>
                        </Card>
                    </Layout.Section>
                    {!activeStore ? (
                        <TransferIndexTable tableHeadings={TABLE_HEADINGS} panelTitle="Store to Store Transfers" locationsAccess={currentUser.locationIds} scannedValue={scannedValue} />
                    ) : (
                        <>
                            <TransferIndexTable tableHeadings={INCOMING_TABLE_HEADINGS} panelTitle="Incoming Store to Store Transfers" incomingLocation={activeStore} scannedValue={scannedValue} />
                            <TransferIndexTable tableHeadings={OUTGOING_TABLE_HEADINGS} panelTitle="Outgoing Store to Store Transfers" outgoingLocation={activeStore} scannedValue={scannedValue} />
                        </>
                    )}
                    <Layout.Section>
                        <Card>
                            <ActivityLog objectType="InventoryTransfer" />
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>
        </div>
    );
}
